import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { History, Loader2, UserPlus, UserMinus } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";

export default function AssignmentsTab({ child, reload, canAssign }) {
  const [items, setItems] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ trainer_id: "", assignment_type: "primary", start_date: "", reason: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/children/${child.id}/assignments`);
      setItems(data.items);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [child.id]);

  useEffect(() => {
    load();
    if (canAssign) {
      api.get("/staff", { params: { role: "travel_trainer", status: "active" } }).then((r) => setTrainers(r.data.items)).catch(() => {});
    }
  }, [load, canAssign]);

  const assign = async (e) => {
    e.preventDefault();
    if (!form.trainer_id) {
      toast.error("Select a trainer");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/children/${child.id}/assignments`, {
        trainer_id: form.trainer_id,
        assignment_type: form.assignment_type,
        start_date: form.start_date || null,
        reason: form.reason || null,
        notes: form.notes || null,
      });
      toast.success("Trainer assigned — they now have access to this case");
      setForm({ trainer_id: "", assignment_type: "primary", start_date: "", reason: "", notes: "" });
      load();
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const end = async (assignmentId) => {
    const reason = window.prompt("Reason for ending this assignment (recorded in audit log):");
    if (reason === null) return;
    try {
      await api.post(`/children/${child.id}/assignments/${assignmentId}/end`, { reason: reason || "Assignment ended" });
      toast.success("Assignment ended — trainer access to this case has been removed");
      load();
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fmt = (v) => {
    try {
      return format(new Date(v), "dd MMM yyyy");
    } catch {
      return v || "—";
    }
  };

  const active = items.filter((a) => a.active);
  const history = items.filter((a) => !a.active);

  return (
    <div className="space-y-4" data-testid="assignments-tab">
      {canAssign && (
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><UserPlus className="h-5 w-5 text-primary" /> Assign trainer</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={assign} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="assignment-form">
              <div className="space-y-1.5">
                <Label>Trainer *</Label>
                <Select value={form.trainer_id} onValueChange={(v) => setForm({ ...form, trainer_id: v })}>
                  <SelectTrigger data-testid="assignment-trainer-select"><SelectValue placeholder="Select trainer" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {trainers.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`assignment-trainer-${t.staff_number}`}>{t.name} ({t.staff_number})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Assignment type</Label>
                <Select value={form.assignment_type} onValueChange={(v) => setForm({ ...form, assignment_type: v })}>
                  <SelectTrigger data-testid="assignment-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="primary" data-testid="assignment-type-primary">Primary trainer</SelectItem>
                    <SelectItem value="secondary" data-testid="assignment-type-secondary">Secondary / cover trainer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Start date</Label>
                <Input type="date" data-testid="assignment-start-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Reason (for reassignment)</Label>
                <Input data-testid="assignment-reason-input" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
              </div>
              <div className="sm:col-span-2 lg:col-span-4 flex gap-3 items-end">
                <div className="space-y-1.5 flex-1">
                  <Label>Notes</Label>
                  <Textarea rows={1} data-testid="assignment-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
                <Button data-testid="assignment-submit-button" type="submit" disabled={saving}
                  className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Assign"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-lg">Current assignments</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : active.length === 0 ? (
            <p className="text-sm text-slate-600" data-testid="assignments-empty">No active assignments. The child is awaiting a trainer.</p>
          ) : (
            <div className="space-y-2" data-testid="assignments-active">
              {active.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3" data-testid={`assignment-active-${a.id}`}>
                  <Badge className={a.assignment_type === "primary" ? "bg-primary text-white border-transparent" : "bg-slate-200 text-slate-700 border-transparent"}>
                    {a.assignment_type === "primary" ? "Primary" : "Secondary / cover"}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm">{a.trainer_name}</p>
                    <p className="text-xs text-slate-500">Assigned by {a.assigned_by_name || "—"} · start {fmt(a.start_date)}</p>
                  </div>
                  {canAssign && (
                    <Button variant="outline" className="h-9 text-xs border-red-300 text-red-700 hover:bg-red-50"
                      data-testid={`assignment-end-${a.id}`} onClick={() => end(a.id)}>
                      <UserMinus className="h-3.5 w-3.5 mr-1" /> End assignment
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><History className="h-5 w-5 text-slate-500" /> Assignment history</CardTitle></CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-slate-600" data-testid="assignments-history-empty">No previous assignments. History is preserved when trainers are reassigned.</p>
          ) : (
            <div className="space-y-2" data-testid="assignments-history">
              {history.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-md border border-dashed border-slate-300 p-3 text-sm" data-testid={`assignment-history-${a.id}`}>
                  <Badge variant="secondary">{a.assignment_type}</Badge>
                  <span className="font-medium text-slate-700">{a.trainer_name}</span>
                  <span className="text-xs text-slate-500">
                    {fmt(a.start_date)} → {fmt(a.end_date)} · {a.reason || "—"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
